import { DateTime } from "luxon";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useResizeDetector } from "react-resize-detector";
import {
	LineSegment,
	VictoryAxis,
	VictoryChart,
	VictoryCursorContainer,
	VictoryGroup,
	VictoryLine,
	VictoryScatter,
	VictoryStyleObject,
} from "victory";
import { DateRange } from "../../data/date-range";
import IconCalendar from "../../dynamicicons/icon-calendar";
import DateRangeSelector from "../../elements/date-range-selector";
import FlatButton from "../../elements/flat-button";
import VelavuSwitch from "../../elements/velavu-switch";
import { makeStyleable } from "../../helper/icon-helper";
import { formatDateRange } from "../../helper/language-helper";
import { inverseLerp, lerp } from "../../helper/math-helper";
import { classArr } from "../../helper/style-helper";
import * as globals from "../../styles/global.icss.scss";
import whitelabel from "../../whitelabel";
import AnalyticsCardHeader from "./analytics-card-header";
import * as styles from "./analytics-graph.module.scss";

const svgPadding = 12;
const svgTextHeight = 24;

const graphPalette: string[] = [
	whitelabel.palette.primary900,
	whitelabel.palette.primary800,
	whitelabel.palette.primary500,
];

export interface AnalyticsGraphPoint {
	x: Date;
	y: number;
}

export interface AnalyticsGraphEntry {
	id: string;
	name: string;
	data: AnalyticsGraphPoint[];
}

const makeStyles = <T extends { [key: string]: VictoryStyleObject }>(t: T) => t;
const victoryStyles = makeStyles({
	parent: {
		fontFamily: "Poppins, sans-serif",
	},
	mainAxis: {
		stroke: whitelabel.palette.secondary100,
	},
	dependentAxis: {
		stroke: "none",
	},
	mainTickLabels: {
		fontFamily: "inherit",
		fill: globals.textTertiary,
	},
	dependentTickLabels: {
		fontFamily: "inherit",
		fill: globals.textSecondary,
	},
	grid: {
		fill: "none",
		stroke: whitelabel.palette.secondary100,
	},
	ticks: {
		fill: "transparent",
		size: 12,
		stroke: whitelabel.palette.secondary100,
		strokeWidth: 1,
	},
	line: {
		strokeWidth: 2,
	},
});

interface CustomLabelProps {
	// Victory props
	// All props are optional because Victory has a bad TypeScript API
	// https://commerce.nearform.com/open-source/victory/docs/victory-label
	x?: number;
	y?: number;
	datum?: {
		x: Date;
		y: number;
	};
	active?: boolean;
	text?: string;

	// Custom props
	data: AnalyticsGraphEntry[];
	unitSuffix: string;
	graphWidth?: number;
}

function CustomLabel(props: CustomLabelProps) {
	type LabelData = { id: string; displayName: string; value: number };

	//Interpolate the data between the points
	const data = useMemo(():
		{ hoverDate: Date; labels: LabelData[] } | undefined => {
		if (props.data === undefined || props.datum === undefined)
			return undefined;

		//Get the date at the hovered point
		const hoverDate = props.datum.x;

		return {
			hoverDate,
			labels: props.data
				.map((graphEntry): LabelData | undefined => {
					//Find the first entry with a date greater than the hover date
					const sortedData = [...graphEntry.data];
					sortedData.sort(
						(d1, d2) => d1.x.getTime() - d2.x.getTime(),
					);
					const upperIndex = sortedData.findIndex(
						(graphPoint) => graphPoint.x > hoverDate,
					);

					//Ignoring if no entry was found, or the entry was the first entry (nothing to average)
					if (upperIndex === -1 || upperIndex === 0) return undefined;

					const lowerEntry = sortedData[upperIndex - 1];
					const upperEntry = sortedData[upperIndex];

					//Average between this entry and the previous one
					return {
						id: graphEntry.id,
						displayName: graphEntry.name,
						value: lerp(
							inverseLerp(
								hoverDate.getTime(),
								lowerEntry.x.getTime(),
								upperEntry.x.getTime(),
							),
							lowerEntry.y,
							upperEntry.y,
						),
					};
				})
				//Exclude non-applicable entries
				.filter((it) => it !== undefined) as LabelData[],
		};
	}, [props.data, props.datum]);

	if (data === undefined) return null;

	let alignToLeft: boolean;
	if (props.x === undefined || props.graphWidth === undefined) {
		alignToLeft = false;
	} else {
		alignToLeft = props.x > props.graphWidth * 0.8;
	}

	const width = Math.max(
		115,
		...data.labels.map(
			(label) =>
				(label.displayName.length +
					(Math.round(label.value * 10) / 10).toFixed(1).toString()
						.length +
					props.unitSuffix.length +
					4) *
				8,
		),
	);
	const height = svgTextHeight * (3 + data.labels.length) - svgPadding;

	const xOffset = 5;
	const yOffset = -10;

	return (
		<g
			style={{
				transform: alignToLeft
					? `translate(${(width + xOffset) * -1}px, ${yOffset}px)`
					: `translate(${xOffset}px, ${yOffset}px)`,
			}}
		>
			<rect
				x={props.x!}
				y={props.y!}
				width={width}
				height={height}
				rx="8"
				fill={whitelabel.palette.secondary900}
			/>
			<text
				x={props.x! + svgPadding}
				y={props.y! + svgTextHeight}
				fontSize="14"
				fontWeight="500"
				fontFamily="inherit"
				fill={whitelabel.palette.secondary50}
			>
				{DateTime.fromJSDate(data.hoverDate).toLocaleString(
					DateTime.DATE_MED,
				)}
			</text>
			<text
				x={props.x! + svgPadding}
				y={props.y! + svgTextHeight * 2}
				fontSize="14"
				fontWeight="500"
				fontFamily="inherit"
				fill={whitelabel.palette.secondary50}
			>
				{DateTime.fromJSDate(data.hoverDate).toLocaleString(
					DateTime.TIME_SIMPLE,
				)}
			</text>
			{data.labels.map((label, i) => (
				<text
					key={label.id}
					x={props.x! + svgPadding}
					y={props.y! + svgTextHeight * (3 + i)}
					fontSize="14"
					fontFamily="inherit"
					fill={whitelabel.palette.secondary500}
				>
					{`${label.displayName}: ${(
						Math.round(label.value * 10) / 10
					).toFixed(1)}${props.unitSuffix}`}
				</text>
			))}
		</g>
	);
}

export interface AnalyticsGraphUnitRange {
	min: number;
	max: number;
}

export interface AnalyticsGraphProps {
	/// The title to show at the top of the graph
	title: string;
	/// The active date range
	dateRange: DateRange;
	/// Updates the active date range
	setDateRange: (range: DateRange) => void;
	/// The lower and upper range of the graph
	unitRange: AnalyticsGraphUnitRange;
	/// The points to show on the graph
	data: AnalyticsGraphEntry[];
	/// The suffix to apply to numeric values in the graph
	unitSuffix: string;
}

/**
 * A generic line graph component that matches Velavu styling.
 * Includes a date range picker.
 */
export function AnalyticsGraph(props: AnalyticsGraphProps) {
	const { t } = useTranslation();
	const [showPoints, setShowPoints] = useState(false);
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const { width, height, ref } = useResizeDetector();

	const togglePickerOpen = useCallback(
		() => setIsPickerOpen((open) => !open),
		[],
	);

	const propsSetDateRange = props.setDateRange;
	const handleApplyDateRange = useCallback(
		(range: DateRange) => {
			propsSetDateRange(range);
			setIsPickerOpen(false);
		},
		[propsSetDateRange, setIsPickerOpen],
	);

	return (
		<>
			<AnalyticsCardHeader title={props.title}>
				<VelavuSwitch
					label={t("analytics.show_points")}
					toggled={showPoints}
					onChange={setShowPoints}
				/>
				<div
					className={classArr(
						styles.rangePickerWrapper,
						isPickerOpen && styles.rangePickerWrapperOpen,
					)}
				>
					<FlatButton
						className={styles.rangePickerButton}
						icon={makeStyleable(IconCalendar)}
						label={formatDateRange(props.dateRange)}
						onClick={togglePickerOpen}
					/>

					<div className={styles.rangePickerPopover}>
						<DateRangeSelector
							range={props.dateRange}
							onApply={handleApplyDateRange}
						/>
					</div>
				</div>
			</AnalyticsCardHeader>
			<div ref={ref} className={styles.graph}>
				<VictoryChart
					width={width}
					height={height}
					style={{ parent: victoryStyles.parent }}
					scale={{ x: "time", y: "linear" }}
					domain={{
						x: [props.dateRange.start, props.dateRange.end],
						y: [props.unitRange.min, props.unitRange.max],
					}}
					containerComponent={
						<VictoryCursorContainer
							cursorDimension="x"
							cursorComponent={
								<LineSegment
									style={{
										stroke: whitelabel.palette.secondary900,
									}}
								/>
							}
							cursorLabel={() => ""}
							cursorLabelComponent={
								<CustomLabel
									data={props.data}
									unitSuffix={props.unitSuffix}
									graphWidth={width}
								/>
							}
							cursorLabelOffset={{ x: 0, y: 0 }}
						/>
					}
				>
					<VictoryAxis
						style={{
							axis: victoryStyles.mainAxis,
							ticks: victoryStyles.ticks,
							tickLabels: victoryStyles.mainTickLabels,
						}}
						standalone={false}
					/>
					<VictoryAxis
						style={{
							axis: victoryStyles.dependentAxis,
							tickLabels: victoryStyles.dependentTickLabels,
							grid: victoryStyles.grid,
						}}
						dependentAxis
						standalone={false}
						tickFormat={(x: number) =>
							Math.round(x * 10) / 10 + props.unitSuffix
						}
						crossAxis={false}
					/>
					<VictoryGroup colorScale={graphPalette}>
						{props.data.map((data) => (
							<VictoryLine
								key={data.id}
								data={data.data}
								interpolation="monotoneX"
								style={{ data: victoryStyles.line }}
							/>
						))}
					</VictoryGroup>
					<VictoryGroup colorScale={graphPalette}>
						{showPoints &&
							props.data.map((data) => (
								<VictoryScatter
									key={data.id}
									style={{ data: { fill: globals.accent } }}
									size={3}
									data={data.data}
								/>
							))}
					</VictoryGroup>
				</VictoryChart>
			</div>
		</>
	);
}
